import React, { useEffect, useMemo, useState } from "react";
import { Building2, Stethoscope } from "lucide-react";
import { SCOPE_TYPES, fetchAllDoctors, fetchOrgs } from "./api";

const MODES = [
    { id: SCOPE_TYPES.ORG, label: "Search Org", icon: Building2 },
    { id: SCOPE_TYPES.DOCTOR, label: "Search Dr", icon: Stethoscope },
];

const RESULT_LIMIT = 50;

const ModeButton = ({ mode, active, onClick }) => {
    const Icon = mode.icon;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold transition-colors ${
                active
                    ? "border-ktx-purple bg-purple-50 text-ktx-purple"
                    : "border-gray-300 bg-white text-gray-700 hover:border-ktx-purple hover:text-ktx-purple"
            }`}
        >
            <Icon className="h-4 w-4" aria-hidden />
            {mode.label}
        </button>
    );
};

const ResultRow = ({ title, meta, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="w-full border-b border-gray-100 px-4 py-2 text-left last:border-b-0 hover:bg-purple-50"
    >
        <span className="font-medium text-gray-900">{title}</span>
        {meta && (
            <span className="ml-2 text-xs font-normal text-gray-500">{meta}</span>
        )}
    </button>
);

const ScopeSearch = ({ onSelect }) => {
    const [mode, setMode] = useState(SCOPE_TYPES.ORG);
    const [query, setQuery] = useState("");
    const [orgs, setOrgs] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([fetchOrgs(), fetchAllDoctors()])
            .then(([orgRows, doctorRows]) => {
                if (cancelled) return;
                setOrgs(Array.isArray(orgRows) ? orgRows : []);
                setDoctors(Array.isArray(doctorRows) ? doctorRows : []);
                setError(null);
            })
            .catch((err) => {
                if (!cancelled) setError(err.message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const search = query.trim().toLowerCase();

    const orgResults = useMemo(() => {
        const named = orgs.filter((org) => org?.name);
        if (!search) return named.slice(0, RESULT_LIMIT);
        return named
            .filter((org) => org.name.toLowerCase().includes(search))
            .slice(0, RESULT_LIMIT);
    }, [orgs, search]);

    const doctorResults = useMemo(() => {
        if (!search) return [];
        return doctors
            .filter((doctor) => {
                const name = doctor.user_name?.toLowerCase() || "";
                const email = doctor.user_email?.toLowerCase() || "";
                const id = String(doctor.user_id || "").toLowerCase();
                return (
                    name.includes(search) ||
                    email.includes(search) ||
                    id.includes(search)
                );
            })
            .slice(0, RESULT_LIMIT);
    }, [doctors, search]);

    const isOrgMode = mode === SCOPE_TYPES.ORG;
    const results = isOrgMode ? orgResults : doctorResults;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
                {MODES.map((option) => (
                    <ModeButton
                        key={option.id}
                        mode={option}
                        active={mode === option.id}
                        onClick={() => {
                            setMode(option.id);
                            setQuery("");
                        }}
                    />
                ))}
            </div>

            <p className="text-sm text-gray-600">
                {isOrgMode
                    ? "Pick an org to edit its dictionary and drill into the specialties inside it."
                    : "Search a doctor to edit their own dictionary, with their specialty and org alongside."}
            </p>

            <input
                type="text"
                autoComplete="off"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={
                    isOrgMode
                        ? "Filter orgs..."
                        : "Type a doctor's name, email, or id..."
                }
                className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-ktx-purple focus:outline-none focus:ring-2 focus:ring-ktx-purple"
            />

            {loading && <p className="text-sm text-gray-500">Loading...</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {!loading && !error && results.length > 0 && (
                <div className="max-h-96 w-full max-w-md overflow-y-auto rounded-md border border-gray-200 bg-white shadow">
                    {isOrgMode
                        ? results.map((org) => (
                              <ResultRow
                                  key={org.name}
                                  title={org.name}
                                  meta={`${org.value} doctor${org.value === 1 ? "" : "s"}`}
                                  onClick={() =>
                                      onSelect({
                                          scopeType: SCOPE_TYPES.ORG,
                                          scopeId: org.name,
                                      })
                                  }
                              />
                          ))
                        : results.map((doctor) => (
                              <ResultRow
                                  key={doctor.user_id}
                                  title={doctor.user_name || doctor.user_id}
                                  meta={doctor.user_email}
                                  onClick={() =>
                                      onSelect({
                                          scopeType: SCOPE_TYPES.DOCTOR,
                                          scopeId: String(doctor.user_id),
                                          label: doctor.user_name,
                                      })
                                  }
                              />
                          ))}
                </div>
            )}

            {!loading && !error && results.length === 0 && (
                <p className="text-sm text-gray-500">
                    {isOrgMode || search
                        ? "No matches."
                        : "Start typing to find a doctor."}
                </p>
            )}
        </div>
    );
};

export default ScopeSearch;
